import PropTypes from "prop-types";
import { InputDateModal } from "core/inputs/input-date/InputDateModal";
import InputSelect from "core/inputs/input-select/InputSelect";
import { FormattedMessage } from "react-intl";
import {
  MODAL_CONTENT,
  POPUP_STATUS,
  SEARCH_TYPE,
  TAG_TYPES,
} from "constants/constants";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { useState, useEffect } from "react";
import {
  onAdd,
  onChange,
  onDelete,
  renderCommonBlock,
} from "utils/tableContentHelper";
import { FORM_INFO, STAFF_FORM_PLACEHOLDERS } from "constants/forms";
import { phoneSize } from "utils/screenSizes";
import { translateHelper } from "utils/helper";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import { ENTITY } from "constants/info-header";
import ModalFooter from "components/modal/ModalFooter";
import AlertPopup from "components/alert-popup/AlertPopup";
import {
  ERROR_AT_LEAST_ONE_COEF,
  ERROR_MISSING_FIELDS,
} from "constants/popup-codes";
import { useUserDatatrackContext } from "api/datatrack/UserDatatrackProvider";
import { updatedByDataHelper } from "utils/modalsHelper";
import { format } from "date-fns";
import SalaryValidationModal from "components/modal/BalancesheetSalaryModal";
import { getSalaryCoefPosWarning } from "api/datatrack/salary/queries";

const ManagePositionCoefficient = ({
  userCoefPositionList,
  getUpdatedValues,
  lang,
  setElementsToDelete,
  contractualStatusList,
  syntecCoef,
  userType,
  onClose,
  onValidate,
  person_id,
  userScope,
  userCurrentSalary,
}) => {
  const { apiErrorResponse, userDatatrackUpdateById } =
    useUserDatatrackContext();
  const [data, setData] = useState(userCoefPositionList);
  const [initialData, setInitialData] = useState(userCoefPositionList);
  const [status, setStatus] = useState();
  const [code, setCode] = useState();
  const [reloadData, setReloadData] = useState(false);
  const [showSalaryValidationModal, setShowSalaryValidationModal] =
    useState(false);
  const [salaryWarning, setSalaryWarning] = useState(null);
  const [lastEditedCoefId, setLastEditedCoefId] = useState(null);
  const [lastEditedCoefRowId, setLastEditedCoefRowId] = useState(null);
  const { id: userCurrentSalaryId, value: userCurrentSalaryValue } =
    userCurrentSalary || {};
  const isStaffProfile = userType?.toLowerCase() === ENTITY.STAFF;
  const coefPositionKey = isStaffProfile
    ? "syntec_coef_position_staff"
    : "syntec_coef_position";

  useEffect(() => {
    if (apiErrorResponse?.["establishment_date"]) {
      setCode("api_error_establishment_date");
      setStatus(POPUP_STATUS.ERROR);
    }
    if (apiErrorResponse?.["syntec_coef_position"]) {
      setCode("api_error_syntec_coef_position");
      setStatus(POPUP_STATUS.ERROR);
    }
  }, [apiErrorResponse]);

  const header = [
    {
      label: {
        key: "status",
        value: (
          <FormattedMessage
            id="input_contractual_status_label"
            defaultMessage="Statut contractuel"
          />
        ),
      },
      icon: "clients",
      width: "fit-content",
    },
    {
      label: {
        key: "syntec_coefficient_id",
        value: (
          <FormattedMessage
            id="input_syntec_coefficient_id_label"
            defaultMessage="Coef/Position"
          />
        ),
      },
      icon: "clients",
      width: "fit-content",
    },
    {
      label: {
        key: "start_date",
        value: (
          <FormattedMessage
            id="start_date"
            defaultMessage="Date d'application"
          />
        ),
      },
      icon: "calendar",
      width: "fit-content",
    },
    {
      label: {
        key: "updated_by",
        value: (
          <FormattedMessage id="updated_by" defaultMessage="Modifié par" />
        ),
      },
      icon: "user",
      width: "fit-content",
    },
    {
      label: {
        key: "updated_at",
        value: <FormattedMessage id="updated_at" defaultMessage="Le" />,
      },
      icon: "calendar",
      width: "fit-content",
    },
  ];

  useEffect(() => {
    if (
      initialData.length < userCoefPositionList.length &&
      userCoefPositionList.length > 0
    ) {
      setData(userCoefPositionList);
      setInitialData(userCoefPositionList);
    }
  }, [initialData, userCoefPositionList]);

  useEffect(() => {
    if (userCoefPositionList?.length && data?.length) {
      updatedByDataHelper({ data, setData, modalData: userCoefPositionList });
    }
  }, [userCoefPositionList]);

  const selectedOptionsFormatter = ({ coefPositionList, coefPositionId }) => {
    if (!coefPositionId) return;
    const coefPosition =
      coefPositionList?.find((e) => e.id === coefPositionId) ||
      syntecCoef.find((e) => e.id === coefPositionId);
    return coefPosition
      ? [{ label: coefPosition.value, value: coefPosition.id }]
      : [];
  };

  const getParams = (elm, val) => {
    return {
      getUpdatedValues,
      id: elm?.id,
      key: coefPositionKey,
      setData,
      data,
      val: val ? val[0]?.value : [],
    };
  };

  const setError = (code) => {
    setCode(code);
    setStatus(POPUP_STATUS.ERROR);
    setReloadData(!reloadData);
  };

  const onClickValidate = async () => {
    if (
      data.some((elm) => {
        return !(
          (elm.syntec_coef_position > 0 ||
            elm.syntec_coef_position_staff > 0) &&
          !!elm.start_date
        );
      })
    ) {
      setError(ERROR_MISSING_FIELDS);
      return;
    }

    if (
      !lastEditedCoefId ||
      salaryWarning?.current_coef_position_id === lastEditedCoefId
    ) {
      onValidate();
      return;
    }

    try {
      if (isStaffProfile) {
        onValidate();
      } else {
        const response = await getSalaryCoefPosWarning({
          scope: userScope,
          person_id,
          salary: userCurrentSalaryValue,
          syntec_coef: lastEditedCoefId,
        });

        const warning = response?.warning;
        if (!warning) {
          onValidate();
          return;
        }
        setSalaryWarning(warning);
        setShowSalaryValidationModal(true);
      }
    } catch (e) {
      console.error("getSalaryCoefPosWarning error from coef modal", e);
      onValidate();
    }
  };

  const handleCoefValidationSubmit = async ({ salary, coef }) => {
    setShowSalaryValidationModal(false);
    const selectedCoef = syntecCoef?.find((c) => c.value === coef);
    const coefId = selectedCoef?.id;

    if (coefId && lastEditedCoefRowId) {
      onChange({
        getUpdatedValues,
        id: lastEditedCoefRowId,
        key: coefPositionKey,
        setData,
        data,
        val: coefId,
      });
    }

    const parsedSalary = salary !== "" && !Number.isNaN(salary) ? salary : null;

    if (parsedSalary !== null && userCurrentSalary) {
      try {
        await userDatatrackUpdateById({
          id: userCurrentSalaryId,
          data: {
            ...userCurrentSalary,
            value: Number(parsedSalary),
          },
          model: MODAL_CONTENT.MANAGE_SALARY,
        });
      } catch (e) {
        console.error("Error updating salary from coef modal", e);
      }
    }
  };

  const setStatusValue = ({ elm, val }) => {
    const updatedData = data.map((row) =>
      String(row.id) === String(elm?.id)
        ? { ...row, status: val, [coefPositionKey]: null }
        : row
    );
    setData(updatedData);
    getUpdatedValues(updatedData);
  };

  const formatContent = (content) =>
    content?.length > 0
      ? content?.map((elm, index) => {
          const dataTestIndex = content.length - 1 - index;
          const coefPositionId =
            elm.syntec_coef_position_staff || elm.syntec_coef_position;

          const contractualStatus =
            elm.status ||
            syntecCoef.find((coef) => coef.id === coefPositionId)
              ?.contractual_status_id;

          const coefPositionOptionsByContractualStatus = syntecCoef
            .filter((e) => contractualStatus === e.contractual_status_id)
            ?.map((coef) => ({
              label: coef?.value,
              value: coef?.id,
            }));

          return {
            id: elm?.id,
            status: (
              <InputSelect
                id="status"
                value={contractualStatus}
                options={contractualStatusList.map((elm) => {
                  return {
                    label: translateHelper({ lang, fr: elm.fr, en: elm.en }),
                    value: elm.id,
                  };
                })}
                name="status"
                setValue={(name, val) => setStatusValue({ elm, val })}
                dataTestId={`${MODAL_CONTENT.MANAGE_POSITION_COEF}_modal-contractual_status-${dataTestIndex}`}
              />
            ),
            syntec_coefficient_id: {
              display: (
                <InputSearchTag
                  setValue={(val) => {
                    onChange(getParams(elm, val));
                    const selected = val?.[0];
                    if (selected) {
                      setLastEditedCoefId(selected.value);
                      setLastEditedCoefRowId(elm?.id);
                    }
                  }}
                  onDelete={() =>
                    onChange({ ...getParams(elm), contractualStatus })
                  }
                  options={coefPositionOptionsByContractualStatus}
                  limitChoice={1}
                  isEmpty={elm.syntec_coefficient_id?.length === 0}
                  searchType={SEARCH_TYPE.ARRAY}
                  popup={FORM_INFO.syntec_coef_position_staff?.popup}
                  selectedOptions={selectedOptionsFormatter({
                    coefPositionList: coefPositionOptionsByContractualStatus,
                    coefPositionId,
                  })}
                  placeholder={STAFF_FORM_PLACEHOLDERS.syntec_coefficient_id}
                  type={TAG_TYPES.POS_COEF}
                  id="syntec_coefficient_id"
                  dataTestId={`${MODAL_CONTENT.MANAGE_POSITION_COEF}_modal-coef_position-${dataTestIndex}`}
                />
              ),
            },
            start_date: (
              <div
                data-testid={`${MODAL_CONTENT.MANAGE_POSITION_COEF}_modal-start_date-${dataTestIndex}`}
              >
                <InputDateModal
                  lang={lang}
                  elm={elm}
                  onChange={(date) => {
                    const formattedDate = date
                      ? format(date, "yyyy-MM-dd")
                      : "";
                    onChange({
                      getUpdatedValues,
                      id: elm?.id,
                      key: "start_date",
                      setData,
                      data,
                      val: formattedDate,
                    });
                  }}
                  getUpdatedValues={getUpdatedValues}
                  id={elm?.id}
                  setData={setData}
                  data={data}
                />
              </div>
            ),
            ...renderCommonBlock({
              data,
              getUpdatedValues,
              id: elm.id,
              onDelete,
              setData,
              updated_at: elm.updated_at,
              updated_by: elm.updated_by,
              lang,
              setElementsToDelete,
              deleteBtnTestId: `${MODAL_CONTENT.MANAGE_POSITION_COEF}_modal-delete_btn-${dataTestIndex}`,
              deleteError: data?.filter((e) => !e.new_row)?.length - 1 === 0,
              errorToDisplay: () => setError(ERROR_AT_LEAST_ONE_COEF),
            }),
          };
        })
      : [];

  return (
    <>
      <button
        className="button-style"
        onClick={() => onAdd({ header, data, setData })}
        data-testid={`${MODAL_CONTENT.MANAGE_POSITION_COEF}_modal-add_coef_position_btn`}
      >
        <Typography
          className="link-1 violet "
          type="h2"
          label={
            <>
              +{" "}
              <FormattedMessage
                id="add_position_coeff"
                defaultMessage="Ajouter une position/coefficient"
              />
            </>
          }
        />
      </button>
      <br />
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <Table
        id="position_coeff"
        header={header}
        content={formatContent(data)}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
      <br />
      <br />
      <ModalFooter
        onClose={onClose}
        onValidate={() => {
          onClickValidate();
          setReloadData(!reloadData);
        }}
        modalType={MODAL_CONTENT.MANAGE_POSITION_COEF}
      />
      <SalaryValidationModal
        open={showSalaryValidationModal}
        warning={salaryWarning}
        coefOptions={syntecCoef?.map((coef) => ({
          label: coef.value,
          value: coef.value,
        }))}
        onClose={() => setShowSalaryValidationModal(false)}
        onSubmit={handleCoefValidationSubmit}
      />
    </>
  );
};

export default ManagePositionCoefficient;

ManagePositionCoefficient.propTypes = {
  userCoefPositionList: PropTypes.object,
  getUpdatedValues: PropTypes.func,
  lang: PropTypes.string,
  setElementsToDelete: PropTypes.func,
  contractualStatusList: PropTypes.array,
  syntecCoef: PropTypes.array,
  userType: PropTypes.string,
  onClose: PropTypes.func,
  onValidate: PropTypes.func,
};
